import { FunctionComponent, useState } from "react";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { Icon, MarkdownContent } from "@/components";
import { DeepResearchResponseSection } from "@/pages/Private/redux/deepResearch/schema";
import { classNames } from "@/utils/classNames";

import { PinnableFact } from "@/pages/Private/redux/deepResearch/usePinnedResearchFacts";

import { ResearchFactRow } from "./ResearchFactRow";
import { SourcePills } from "./SourcePills";
import { buildFactKey, resolveTalkingPoints } from "./researchSections.utils";

export interface SectionBodyProps {
	sectionKey: string;
	section: DeepResearchResponseSection;
	pinnedKeys: Set<string>;
	isPinning: boolean;
	isSelecting: boolean;
	portalContainer?: HTMLElement | null;
	onTogglePin: (fact: PinnableFact) => void;
	/** Opens the lead's Chat tab from a pinned fact (PREC-3680). */
	onRequestSequence?: () => void;
}

const Bullet: FunctionComponent = () => (
	<span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-light" />
);

const PointNumber: FunctionComponent<{ value: number }> = ({ value }) => (
	<span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-light-blue text-[11px] font-bold text-brand-light">
		{value}
	</span>
);

/** Talking Points: one numbered, individually citable and pinnable row per point. */
export const TalkingPointsBody: FunctionComponent<SectionBodyProps> = ({
	sectionKey,
	section,
	pinnedKeys,
	isPinning,
	isSelecting,
	portalContainer,
	onRequestSequence,
	onTogglePin,
}) => {
	const points = resolveTalkingPoints(section);

	if (points.length === 0) {
		return <MarkdownContent className="text-sm text-text-meta" content={section.content ?? ""} />;
	}

	return (
		<div className="flex flex-col gap-0.5">
			{points.map((point, index) => {
				const factKey = buildFactKey(sectionKey, index);

				return (
					<ResearchFactRow
						key={factKey}
						fact={{
							factKey,
							factLabel: point.title,
							factText: point.text,
							sources: point.sources,
						}}
						isPinned={pinnedKeys.has(factKey)}
						isPinning={isPinning}
						isSuppressed={isSelecting}
						marker={<PointNumber value={index + 1} />}
						portalContainer={portalContainer}
						onRequestSequence={onRequestSequence}
						onTogglePin={onTogglePin}
					>
						<p className="text-sm leading-relaxed text-text-meta">
							{point.title && <span className="font-semibold text-text">{point.title}: </span>}
							{point.text}
						</p>
					</ResearchFactRow>
				);
			})}
		</div>
	);
};

/** Insights: collapsible sub-sections, each a pinnable fact with its own sources. */
export const InsightsBody: FunctionComponent<SectionBodyProps> = ({
	sectionKey,
	section,
	pinnedKeys,
	isPinning,
	isSelecting,
	portalContainer,
	onRequestSequence,
	onTogglePin,
}) => {
	const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
	const subsections = [...(section.subsections ?? [])].sort((a, b) => a.order - b.order);

	if (subsections.length === 0) {
		return <MarkdownContent className="text-sm text-text-meta" content={section.content ?? ""} />;
	}

	return (
		<div className="flex flex-col gap-2">
			{subsections.map((subsection, index) => {
				const factKey = buildFactKey(sectionKey, index);
				const isCollapsed = collapsed[index] ?? false;

				return (
					<div key={factKey} className="rounded-lg border border-border-light">
						<button
							aria-expanded={!isCollapsed}
							className="flex w-full cursor-pointer items-center gap-2.5 px-3 py-2.5 text-left"
							data-testid={`research-insight-${index}`}
							type="button"
							onClick={() => setCollapsed((prev) => ({ ...prev, [index]: !isCollapsed }))}
						>
							<span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-light-blue text-[11px] font-bold text-brand-light">
								{index + 1}
							</span>
							<span className="flex-1 text-sm font-semibold text-text">{subsection.header}</span>
							<Icon
								className={classNames("h-3 w-3 transition-transform", isCollapsed && "-rotate-90")}
								icon={faChevronDown}
							/>
						</button>

						{!isCollapsed && (
							<div className="px-3 pb-2 pl-10">
								<ResearchFactRow
									fact={{
										factKey,
										factLabel: subsection.header,
										factText: subsection.content,
										sources: subsection.sources,
									}}
									isPinned={pinnedKeys.has(factKey)}
									isPinning={isPinning}
									isSuppressed={isSelecting}
									portalContainer={portalContainer}
									onRequestSequence={onRequestSequence}
									onTogglePin={onTogglePin}
								>
									<MarkdownContent
										className="text-sm leading-relaxed text-text-meta"
										content={subsection.content}
									/>
								</ResearchFactRow>
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
};

/** Executive summary: highlighted block, pinnable as a whole. */
export const SummaryBody: FunctionComponent<SectionBodyProps> = ({
	sectionKey,
	section,
	pinnedKeys,
	isPinning,
	isSelecting,
	portalContainer,
	onRequestSequence,
	onTogglePin,
}) => {
	const factKey = buildFactKey(sectionKey, "content");

	return (
		<ResearchFactRow
			fact={{ factKey, factText: section.content ?? "", sources: section.sources }}
			isPinned={pinnedKeys.has(factKey)}
			isPinning={isPinning}
			isSuppressed={isSelecting}
			portalContainer={portalContainer}
			onRequestSequence={onRequestSequence}
			onTogglePin={onTogglePin}
		>
			<div className="rounded-lg border border-brand-light/30 bg-light-blue/60 px-3 py-2.5">
				<MarkdownContent
					className="text-sm leading-relaxed text-text"
					content={section.content ?? ""}
				/>
			</div>
		</ResearchFactRow>
	);
};

/**
 * Anything the panel does not have a dedicated layout for: the section's
 * Markdown, its subsections, and one pin target for the section body. Keeps
 * user-authored prompt templates rendering sensibly.
 */
export const GenericSectionBody: FunctionComponent<SectionBodyProps> = ({
	sectionKey,
	section,
	pinnedKeys,
	isPinning,
	isSelecting,
	portalContainer,
	onRequestSequence,
	onTogglePin,
}) => {
	const factKey = buildFactKey(sectionKey, "content");
	const subsections = [...(section.subsections ?? [])].sort((a, b) => a.order - b.order);

	return (
		<div className="flex flex-col gap-1">
			{!!section.content && (
				<ResearchFactRow
					fact={{ factKey, factText: section.content, sources: section.sources }}
					isPinned={pinnedKeys.has(factKey)}
					isPinning={isPinning}
					isSuppressed={isSelecting}
					portalContainer={portalContainer}
					onRequestSequence={onRequestSequence}
					onTogglePin={onTogglePin}
				>
					<MarkdownContent
						className="text-sm leading-relaxed text-text-meta"
						content={section.content}
					/>
				</ResearchFactRow>
			)}

			{subsections.map((subsection, index) => {
				const subFactKey = buildFactKey(sectionKey, "sub", index);

				return (
					<ResearchFactRow
						key={subFactKey}
						fact={{
							factKey: subFactKey,
							factLabel: subsection.header,
							factText: subsection.content,
							sources: subsection.sources,
						}}
						isPinned={pinnedKeys.has(subFactKey)}
						isPinning={isPinning}
						isSuppressed={isSelecting}
						marker={<Bullet />}
						portalContainer={portalContainer}
						onRequestSequence={onRequestSequence}
						onTogglePin={onTogglePin}
					>
						<p className="text-sm font-semibold text-text">{subsection.header}</p>
						<MarkdownContent
							className="text-sm leading-relaxed text-text-meta"
							content={subsection.content}
						/>
					</ResearchFactRow>
				);
			})}

			{!section.content && subsections.length === 0 && !!section.sources?.length && (
				<SourcePills portalContainer={portalContainer} sources={section.sources} />
			)}
		</div>
	);
};
